// prettier-ignore
class PredictiveSearch extends SearchForm {
	constructor() {
		super();
		this.cachedResults = {};
		this.predictiveSearchResults = this.querySelector(
			"[data-predictive-search]",
		);
		this.allPredictiveSearchInstances =
			document.querySelectorAll("predictive-search");
		this.isOpen = false;
		this.abortController = new AbortController();
		this.searchTerm = "";
		this.body = document.querySelector("body");
		this.header = document.querySelector(".shopify-section-header");
		this.headerBottom = document.querySelector(".header__bottom");
		this.promoModal = document.querySelector(".search__modal");
		this.collectionList = document.querySelector(".template-404__collections");
		this.headerInput = document.querySelector(".header__search .search__input");
		this.calcHeight();
		this.setupEventListeners();
	}

	calcHeight() {
		if (document.querySelector(".predictive-search")) {
			document.documentElement.style.setProperty(
				"--search-height",
				`calc(100vh - ${this.header.getBoundingClientRect().top}px)`,
			);
		}
	}

	setupEventListeners() {
		this.input.form.addEventListener("submit", this.onFormSubmit.bind(this));
		this.input.addEventListener("focus", this.onFocus.bind(this));
		this.addEventListener("focusout", this.onFocusOut.bind(this));
		this.addEventListener("keyup", this.onKeyup.bind(this));
	}

	getQuery() {
		return this.input.value.trim();
	}

	onChange() {
		const newSearchTerm = this.getQuery();
		if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
			// Remove the results when they are no longer relevant for the new search term
			// so they don't show up when the dropdown opens again
			this.querySelector("#predictive-search-results-groups-wrapper")?.remove();
		}

		// Update the term asap, don't wait for the predictive search query to finish loading
		this.updateSearchForTerm(this.searchTerm, newSearchTerm);

		this.searchTerm = newSearchTerm;

		if (!this.searchTerm.length) {
			this.close(true);
			return;
		}

		this.getSearchResults(this.searchTerm);
		if (this.getQuery().length) {
			this.hidePromoBlock();
		} else {
			this.showPromoBlock();
		}
	}

	onFormSubmit(event) {
		if (
			!this.getQuery().length ||
			this.querySelector('[aria-selected="true"] a') ||
			event.submitter.classList.contains("card__link")
		)
			event.preventDefault();
	}

	onFormReset(event) {
		super.onFormReset(event);
		if (super.shouldResetForm()) {
			this.searchTerm = "";
			this.abortController.abort();
			this.abortController = new AbortController();
			this.closeResults(true);
		}
	}

	onFocus() {
		const currentSearchTerm = this.getQuery();
		if (this.classList.contains("search-modal__form")) {
			if (this.headerBottom) {
				this.headerBottom.classList.add("header__bottom--visible");
			}
			if (this.promoModal) {
				this.promoModal.classList.remove("search__modal--hidden");
			}
			if (this.collectionList) {
				this.collectionList.classList.remove("hidden");
			}
		}

		if (!currentSearchTerm.length) {
			return;
		}

		if (this.searchTerm !== currentSearchTerm) {
			// Search term was changed from other search input, treat it as a user change
			this.onChange();
		} else if (this.getAttribute("results") === "true") {
			this.open();
			if (this.promoModal) {
				this.promoModal.classList.add("search__modal--hidden");
			}
			if (this.collectionList) {
				this.collectionList.classList.add("hidden");
			}
		} else {
			this.getSearchResults(this.searchTerm);
		}
	}

	onFocusOut() {
		if (this.classList.contains("search-modal__form")) {
			if (this.headerBottom) {
				this.headerBottom.classList.remove("header__bottom--visible");
			}
		}
		if (!this.classList.contains("search-modal__form")) {
			setTimeout(() => {
				if (!this.contains(document.activeElement)) this.close(true);
			});
		}
	}

	onBlur() {
		this.headerInput.blur();
	}

	onKeyup(event) {
		if (!this.getQuery().length) this.close(true);
		event.preventDefault();

		switch (event.code) {
			case "Enter":
				event.preventDefault();
				this.selectOption();
				break;
			case "Escape":
				this.close(true);
				this.closeResults(clearSearchTerm);
				if (this.getQuery().length) {
					this.hidePromoBlock();
				} else {
					this.showPromoBlock();
				}
				break;
		}
	}

	updateSearchForTerm(previousTerm, newTerm) {
		const searchForTextElement = this.querySelector(
			"[data-predictive-search-search-for-text]",
		);
		const currentButtonText = searchForTextElement?.innerText;
		if (currentButtonText) {
			if (
				currentButtonText.matchAll(new RegExp(previousTerm, "g")).length > 1
			) {
				// The new term matches part of the button text and not just the search term, do not replace to avoid mistakes
				return;
			}
			const newButtonText = currentButtonText.replace(previousTerm, newTerm);
			searchForTextElement.innerText = newButtonText;
		}
		if (this.getQuery().length) {
			this.hidePromoBlock();
		} else {
			this.showPromoBlock();
		}
	}

	selectOption() {
		const selectedOption = this.querySelector(
			'[aria-selected="true"] a, button[aria-selected="true"]',
		);

		if (selectedOption) selectedOption.click();
	}

	getSearchResults(searchTerm) {
		const queryKey = searchTerm.replace(" ", "-").toLowerCase();
		this.setLiveRegionLoadingState();

		if (this.cachedResults[queryKey]) {
			this.renderSearchResults(this.cachedResults[queryKey]);
			this.clickSearchTabs();
			this.calcHeight();
			return;
		}

		if (this.promoModal && this.classList.contains("search-modal__form")) {
			this.promoModal.classList.add("search__modal--hidden");
		}

		if (this.collectionList) {
			this.collectionList.classList.add("hidden");
		}

		fetch(
			`${routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&section_id=predictive-search`,
			{ signal: this.abortController.signal },
		)
			.then((response) => {
				if (!response.ok) {
					var error = new Error(response.status);
					this.close();
					throw error;
				}
				return response.text();
			})
			.then((text) => {
				const resultsMarkup = new DOMParser()
					.parseFromString(text, "text/html")
					.querySelector("#shopify-section-predictive-search").innerHTML;

				// Predictive Search does not index variant SKUs. When it returns no
				// products, fall back to the regular Search API (which does) so typing
				// an SKU shows live results without pressing enter.
				return this.withSkuFallback(searchTerm, resultsMarkup);
			})
			.then((resultsMarkup) => {
				// Save bandwidth keeping the cache in all instances synced
				this.allPredictiveSearchInstances.forEach(
					(predictiveSearchInstance) => {
						predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
					},
				);
				this.renderSearchResults(resultsMarkup);
				this.clickSearchTabs();
				this.calcHeight();
				try {
					colorSwatches();
				} catch (err) {}
			})
			.catch((error) => {
				if (error?.code === 20) {
					// Code 20 means the call was aborted
					return;
				}
				this.close();
				throw error;
			});


		if (this.getQuery().length) {
			this.hidePromoBlock();
		} else {
			this.showPromoBlock();
		}
	}

	// Fetches the regular Search API (indexes variant SKUs) when predictive
	// search found no products, and returns that markup instead.
	withSkuFallback(searchTerm, resultsMarkup) {
		const hasProducts = this.hasProductResults(resultsMarkup);
		if (hasProducts) return Promise.resolve(resultsMarkup);

		this.renderSkuFallbackLoading(searchTerm);

		const urls = this.buildSkuFallbackQueries(searchTerm).map((query) =>
			this.buildSkuFallbackUrl(query),
		);

		return this.fetchSkuFallback(urls, 0, null, searchTerm)
			.then((markup) => markup || this.buildSkuFallbackEmptyMarkup(searchTerm))
			.catch(() => this.buildSkuFallbackEmptyMarkup(searchTerm));
	}

	// SKUs are stored inconsistently (dashes, spaces, suffixes like -BLK), so we
	// try the normal full Search API shapes first. The Search API already uses
	// options[fields]=variants.sku, so field-qualified SKU queries are avoided
	// here because they are slower and inconsistent across Shopify themes.
	buildSkuFallbackQueries(searchTerm) {
		const raw = String(searchTerm).trim();
		const compact = raw.replace(/[\s._/-]+/g, "");
		const dashed = compact.replace(/([A-Za-z]+)(\d)/, "$1-$2");
		const spaced = compact.replace(/([A-Za-z]+)(\d)/, "$1 $2");
		const wildcard = raw.endsWith("*") ? raw : `${raw}*`;
		const queries = [raw, compact, dashed, spaced, wildcard].filter(Boolean);

		return queries.filter((q, i) => queries.indexOf(q) === i);
	}

	hasProductResults(markup) {
		if (!markup) return false;
		const doc = new DOMParser().parseFromString(markup, "text/html");
		return Boolean(
			doc.querySelector(".predictive-search__results-list_products li"),
		);
	}

	buildSkuFallbackUrl(searchTerm) {
		const searchUrl = (window.routes && window.routes.search_url) || "/search";
		const params = new URLSearchParams();
		params.set("q", searchTerm);
		params.set("type", "product");
		params.set("options[prefix]", "last");
		params.set("options[unavailable_products]", "show");
		params.set(
			"options[fields]",
			"title,product_type,variants.title,vendor,variants.sku,tag",
		);
		params.set("view", "wc-sku");
		return `${searchUrl}?${params.toString()}`;
	}


	fetchSkuFallback(urls, index, emptyMarkup, searchTerm) {
		const url = urls[index];
		if (!url) return Promise.resolve(emptyMarkup);

		return fetch(url, { signal: this.abortController.signal })
			.then((response) => (response.ok ? response.text() : null))
			.then((text) => {
				const markup = this.extractSkuFallbackMarkup(text, searchTerm);
				if (this.hasProductResults(markup)) return markup;
				return this.fetchSkuFallback(
					urls,
					index + 1,
					markup || emptyMarkup,
					searchTerm,
				);
			})
			.catch(() => this.fetchSkuFallback(urls, index + 1, emptyMarkup, searchTerm));
	}

	extractSkuFallbackMarkup(text, searchTerm) {
		if (!text) return this.buildSkuFallbackEmptyMarkup(searchTerm);
		const doc = new DOMParser().parseFromString(text, "text/html");
		const fallbackFragment = doc.querySelector(
			"[data-wc-sku-fallback-fragment]",
		);
		if (fallbackFragment) return fallbackFragment.innerHTML;

		// Some Shopify stores ignore alternate search templates and return the full
		// search page for ?view=wc-sku. In that case, lift the product cards from the
		// rendered search results and wrap them in the predictive-search structure.
		const productCards = Array.from(
			doc.querySelectorAll(
				"#product-grid .collection-product-list > li.collection-product-card, .template-search__results .collection-product-list > li.collection-product-card",
			),
		);
		if (productCards.length) {
			return this.buildSkuFallbackMarkupFromCards(productCards, searchTerm);
		}

		return this.buildSkuFallbackEmptyMarkup(searchTerm);
	}

	buildSkuFallbackMarkupFromCards(productCards, searchTerm) {
		const escapedTerm = this.escapeHtml(searchTerm);
		const cardsMarkup = productCards
			.slice(0, 6)
			.map((card, index) => {
				const clone = card.cloneNode(true);
				clone.id = `predictive-search-option-product-${index + 1}`;
				clone.classList.add("predictive-search__list-item");
				clone.setAttribute("role", "option");
				clone.setAttribute("aria-selected", "false");
				return clone.outerHTML;
			})
			.join("");

		return `
			<div id="predictive-search-results" role="listbox" tabindex="-1">
				<div id="predictive-search-results-groups-wrapper" class="predictive-search__results-groups-wrapper predictive-search__results-groups-wrapper--no-suggestions">
					<div class="predictive-search__result-group">
						<div class="predictive-search__result-tabs">
							<div class="predictive-search__result-tab active" data-type-target="products">
								<span class="predictive-search__result-tab-title h4">Products</span>
							</div>
						</div>
						<div>
							<ul id="predictive-search-results-products-list" class="predictive-search__results-list predictive-search__results-list_products list-unstyled" role="group" aria-labelledby="predictive-search-products" tabindex="-1" data-type="products">
								${cardsMarkup}
							</ul>
						</div>
					</div>
				</div>
			</div>
			<span class="hidden" data-predictive-search-live-region-count-value>${productCards.length} product matches found for ${escapedTerm}</span>
		`;
	}

	renderSkuFallbackLoading(searchTerm) {
		this.predictiveSearchResults.innerHTML = `
			<div id="predictive-search-results" role="listbox" tabindex="-1">
				<div class="predictive-search__sku-state predictive-search__sku-loading-state" aria-live="polite">
					<div class="predictive-search__sku-spinner">${this.getSpinnerMarkup()}</div>
					<div>
						<p class="predictive-search__sku-title">Searching the full product catalog…</p>
						<p class="predictive-search__sku-text">Checking product titles and variant SKUs for “${this.escapeHtml(searchTerm)}”.</p>
					</div>
				</div>
			</div>
			<span class="hidden" data-predictive-search-live-region-count-value>Searching the full product catalog for ${this.escapeHtml(searchTerm)}</span>
		`;
		this.setAttribute("results", true);
		this.open();
	}

	buildSkuFallbackEmptyMarkup(searchTerm) {
		const escapedTerm = this.escapeHtml(searchTerm);
		return `
			<div id="predictive-search-results" role="listbox" tabindex="-1">
				<div class="predictive-search__sku-state predictive-search__sku-empty-state" aria-live="polite">
					<p class="predictive-search__sku-title">No product or SKU match found</p>
					<p class="predictive-search__sku-text">We searched the full product catalog, including variant SKUs, for “${escapedTerm}”. Try another product name or SKU.</p>
				</div>
			</div>
			<span class="hidden" data-predictive-search-live-region-count-value>No product or SKU match found for ${escapedTerm}</span>
		`;
	}

	getSpinnerMarkup() {
		return this.querySelector(".predictive-search__loading-state")?.innerHTML || "";
	}

	escapeHtml(value) {
		return String(value)
			.replace(/&/g, "&amp;")
			.replace(/</g, "&lt;")
			.replace(/>/g, "&gt;")
			.replace(/"/g, "&quot;")
			.replace(/'/g, "&#039;");
	}



	setLiveRegionLoadingState() {
		this.statusElement =
			this.statusElement || this.querySelector(".predictive-search-status");
		this.loadingText =
			this.loadingText || this.getAttribute("data-loading-text");

		this.setLiveRegionText(this.loadingText);
		this.setAttribute("loading", true);
	}

	setLiveRegionText(statusText) {
		this.statusElement.setAttribute("aria-hidden", "false");
		this.statusElement.textContent = statusText;

		setTimeout(() => {
			this.statusElement.setAttribute("aria-hidden", "true");
		}, 1000);
	}

	renderSearchResults(resultsMarkup) {
		this.predictiveSearchResults.innerHTML = resultsMarkup;
		this.setAttribute("results", true);

		this.setLiveRegionResults();
		this.open();
		if (this.promoModal && this.classList.contains("search-modal__form")) {
			this.promoModal.classList.add("search__modal--hidden");
		}
		if (this.collectionList) {
			this.collectionList.classList.add("hidden");
		}
	}

	clickSearchTabs() {
		const results = this.querySelectorAll(".predictive-search__results-list");
		if (results.length != 0) {
			results[0].classList.add("active");
			this.querySelector(".predictive-search__result-tab").classList.add(
				"active",
			);
			this.querySelectorAll(".predictive-search__result-tab").forEach((tab) => {
				tab.addEventListener("click", (event) => {
					event.preventDefault();
					const typeTarget = tab.dataset.typeTarget;
					this.querySelectorAll(".predictive-search__result-tab").forEach(
						(element) => {
							element.classList.remove("active");
						},
					);
					tab.classList.add("active");

					results.forEach((element) => {
						let resultsType = element.dataset.type;
						if (resultsType == typeTarget) {
							element.classList.add("active");
						} else {
							element.classList.remove("active");
						}
					});
				});
			});
		}
	}

	hidePromoBlock() {
		if (this.classList.contains("search-modal__form")) {
			if (this.promoModal) {
				this.promoModal.classList.add("search__modal--hidden");
			}
			if (this.collectionList) {
				this.collectionList.classList.add("hidden");
			}
		}
	}

	showPromoBlock() {
		if (this.classList.contains("search-modal__form")) {
			if (this.promoModal) {
				this.promoModal.classList.remove("search__modal--hidden");
			}
			if (this.collectionList) {
				this.collectionList.classList.remove("hidden");
			}
		}
	}

	setLiveRegionResults() {
		this.removeAttribute("loading");
		this.setLiveRegionText(
			this.querySelector("[data-predictive-search-live-region-count-value]")
				.textContent,
		);
	}

	open() {
		this.setAttribute("open", true);
		this.input.setAttribute("aria-expanded", true);
		this.isOpen = true;
		if (this.getQuery().length) {
			this.hidePromoBlock();
		} else {
			this.showPromoBlock();
		}
	}

	close(clearSearchTerm = false) {
		this.closeResults(clearSearchTerm);
		this.isOpen = false;
	}

	closeResults(clearSearchTerm = false) {
		if (clearSearchTerm) {
			this.input.value = "";
			this.removeAttribute("results");
		}
		const selected = this.querySelector('[aria-selected="true"]');

		if (selected) selected.setAttribute("aria-selected", false);

		this.input.setAttribute("aria-activedescendant", "");
		this.removeAttribute("loading");
		this.removeAttribute("open");
		this.input.setAttribute("aria-expanded", false);
		this.predictiveSearchResults.removeAttribute("style");
		if (this.getQuery().length) {
			this.hidePromoBlock();
		} else {
			this.showPromoBlock();
		}
	}
}

customElements.define("predictive-search", PredictiveSearch);
